// iter216 · OwnerToolbar
//
// Renders three owner-only elements on a public profile page (Vendor or
// Business):
//   • DASHBOARD toggle at the top — Business (default) | User
//     - "Business" side shows an Edit button.
//     - "User" side navigates the owner to Ride Mode (`/`) so they see
//       exactly what a customer sees. No floating pill anywhere.
//   • Optional inline Edit button (only in Business mode).
//   • Persistent "Back to Home" gold outline footer at the bottom.
//
// Guests + regular logged-in users never see any of this. Presence is
// gated by `isOwner` — the parent computes it from the vendor/business
// `owner_user_id` vs the current user id.
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Pencil } from "lucide-react";
import { useDashboardI18n } from "../lib/useDashboardI18n";
import { markOwnerAsCustomer } from "./OwnerAsCustomerRibbon";

const CREAM = "#F5F1E8", GOLD = "#E8B84B", MUTED = "#9A9490";

/**
 * Top-of-page DASHBOARD toggle (owner-only). Reads `?mode=user` off the
 * current URL — the "User" tab uses a plain <Link> so React-Router
 * updates the URL without a full navigation, and the profile page below
 * hides all Edit affordances whenever the query flag is set.
 */
export function OwnerDashboardToggle({ isOwner, ownerDashboardPath }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { t } = useDashboardI18n("vendor");
  if (!isOwner) return null;
  const params = new URLSearchParams(loc.search);
  const mode = params.get("mode") === "user" ? "user" : "business";

  const goBusiness = () => {
    const p = new URLSearchParams(loc.search); p.delete("mode");
    nav(`${loc.pathname}${p.toString() ? `?${p}` : ""}`, { replace: true });
  };
  // "User" side takes the owner straight to Ride Mode so they see the
  // customer experience. No pill, no floating anything — a small ribbon
  // on the home page reminds them they're in customer view and links
  // back to their dashboard.
  const goUser = () => {
    markOwnerAsCustomer(ownerDashboardPath || "/");
    nav("/");
  };

  const btn = (active) => ({
    flex: 1, minHeight: 48, padding: "10px 20px",
    background: active ? GOLD : "transparent",
    color: active ? "#0B0B0B" : GOLD,
    border: "none",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14, fontWeight: 600, borderRadius: 999,
  });

  return (
    <div data-testid="owner-dashboard-toggle"
         className="w-full flex flex-col items-center gap-2 mb-4">
      <span
        style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 11,
          letterSpacing: 2, textTransform: "uppercase", color: MUTED,
        }}
      >
        {t("dashboard_label") || "DASHBOARD"}
      </span>
      <div
        role="tablist"
        style={{
          background: "#171717",
          border: `1px solid rgba(232,184,75,0.35)`,
          borderRadius: 999, padding: 4, display: "flex", gap: 4,
          width: "min(320px, 100%)",
        }}
      >
        <button
          data-testid="owner-toggle-business"
          role="tab" aria-selected={mode === "business"}
          onClick={goBusiness} style={btn(mode === "business")}
        >
          {t("dashboard_business") || "Business"}
        </button>
        <button
          data-testid="owner-toggle-user"
          role="tab" aria-selected={mode === "user"}
          onClick={goUser} style={btn(mode === "user")}
        >
          {t("dashboard_user") || "User"}
        </button>
      </div>
    </div>
  );
}

/**
 * Small gold outlined Edit button, rendered top-right of the profile
 * header. Only visible on the "Business" side (mode !== "user").
 */
export function OwnerEditButton({ isOwner, onClick }) {
  const loc = useLocation();
  const { t } = useDashboardI18n("vendor");
  if (!isOwner) return null;
  const params = new URLSearchParams(loc.search);
  if (params.get("mode") === "user") return null;
  return (
    <button
      data-testid="owner-edit-button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5"
      style={{
        minHeight: 48, padding: "10px 18px", borderRadius: 999,
        background: "transparent", color: GOLD,
        border: `1px solid ${GOLD}`,
        fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
      }}
    >
      <Pencil className="w-4 h-4" strokeWidth={1.5} />
      {t("edit") || "Edit"}
    </button>
  );
}

/**
 * Persistent "Back to Home" gold outline button anchored at the bottom
 * of the profile page. Shown for everyone (guests, users, owners) so
 * every profile page has a clear return path. The owner explicitly
 * asked for this to always be visible.
 */
export function BackToHomeFooter() {
  const { t } = useDashboardI18n("vendor");
  return (
    <div
      data-testid="back-to-home-footer"
      className="w-full flex justify-center py-8"
    >
      <Link
        to="/"
        data-testid="back-to-home-link"
        className="inline-flex items-center gap-2"
        style={{
          minHeight: 48, padding: "12px 28px", borderRadius: 999,
          background: "transparent", color: GOLD,
          border: `1px solid ${GOLD}`,
          fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600,
        }}
      >
        <Home className="w-4 h-4" strokeWidth={1.5} />
        {t("back_to_home") || "Back to Home"}
      </Link>
    </div>
  );
}
